import { useState, useCallback, useEffect } from 'react';
import type { Airport } from '@/types';
import { searchAirports } from '@/services/airportService';
import { debounce } from '@/utils';
import { useAuth } from './useAuth';

interface UseAirportSearchResult {
  query: string;
  setQuery: (query: string) => void;
  results: Airport[];
  isSearching: boolean;
  error: string | null;
  selectedAirport: Airport | null;
  setSelectedAirport: (airport: Airport | null) => void;
  clearSearch: () => void;
}

export function useAirportSearch(): UseAirportSearchResult {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Airport[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAirport, setSelectedAirport] = useState<Airport | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([]);
        setIsSearching(false);
        return;
      }

      if (!isAuthenticated) {
        setError('Please log in to search airports');
        setResults([]);
        setIsSearching(false);
        return;
      }

      try {
        setError(null);
        const matches = await searchAirports(searchQuery);
        setResults(matches);
      } catch (err) {
        console.error('Airport search error:', err);
        setError('Failed to search airports');
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [isAuthenticated]
  );

  useEffect(() => {
    if (query.length >= 2) {
      setIsSearching(true);
    }
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setSelectedAirport(null);
    setError(null);
  }, []);

  return {
    query,
    setQuery,
    results,
    isSearching,
    error,
    selectedAirport,
    setSelectedAirport,
    clearSearch,
  };
}
